import { useState, type FormEvent } from "react";
import { ArrowLeft, CheckCircle2, Loader2 } from "lucide-react";
import { ApiError } from "@/lib/api";
import { signup } from "./api";

const MIN_PASSWORD = 12;
const input =
  "h-10 w-full rounded-md border border-input bg-background px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring";

/**
 * Customer self-registration on the login page.
 *
 * A signup provisions a brand-new customer company plus a portal login for it,
 * so a new account can only ever see the empty workspace it just created — never
 * another customer's data. The account is active immediately; the caller is
 * returned to the sign-in form with a confirmation.
 */
export function SignupForm({
  onBack,
  onRegistered,
}: {
  onBack: () => void;
  onRegistered?: (email: string) => void;
}) {
  const [fullName, setFullName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [city, setCity] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState<string | null>(null);

  const tooShort = password.length > 0 && password.length < MIN_PASSWORD;
  const mismatch = confirm.length > 0 && confirm !== password;
  const canSubmit =
    fullName.trim().length >= 2 &&
    company.trim().length >= 2 &&
    email.trim().length > 0 &&
    phone.trim().length >= 6 &&
    password.length >= MIN_PASSWORD &&
    password === confirm;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!canSubmit) return;
    setLoading(true);
    try {
      const res = await signup({
        full_name: fullName.trim(),
        company_name: company.trim(),
        email: email.trim(),
        phone: phone.trim(),
        password,
        ...(city.trim() ? { city: city.trim() } : {}),
      });
      onRegistered?.(email.trim());
      setDone(res.message);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Could not create your account.",
      );
    } finally {
      setLoading(false);
    }
  }

  if (done) {
    return (
      <div className="space-y-4 rounded-lg border border-border bg-card p-6 text-center">
        <CheckCircle2 className="mx-auto h-8 w-8 text-sla-ok" aria-hidden="true" />
        <p className="text-sm">{done}</p>
        <button
          type="button"
          onClick={onBack}
          className="inline-flex h-10 w-full items-center justify-center rounded-md bg-primary text-sm font-medium text-primary-foreground hover:opacity-90"
        >
          Back to sign in
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4 rounded-lg border border-border bg-card p-6">
      <p className="text-sm text-muted-foreground">
        Create a customer account to raise and track your support requests.
      </p>

      <form onSubmit={handleSubmit} className="space-y-3" noValidate>
        <div className="space-y-1.5">
          <label htmlFor="su_name" className="text-sm font-medium">
            Your name
          </label>
          <input
            id="su_name"
            type="text"
            autoComplete="name"
            className={input}
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
          />
        </div>

        <div className="space-y-1.5">
          <label htmlFor="su_company" className="text-sm font-medium">
            Company name
          </label>
          <input
            id="su_company"
            type="text"
            autoComplete="organization"
            className={input}
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            required
          />
        </div>

        <div className="space-y-1.5">
          <label htmlFor="su_email" className="text-sm font-medium">
            Work email
          </label>
          <input
            id="su_email"
            type="email"
            autoComplete="username"
            className={input}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <label htmlFor="su_phone" className="text-sm font-medium">
              Contact number
            </label>
            <input
              id="su_phone"
              type="tel"
              autoComplete="tel"
              className={input}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="su_city" className="text-sm font-medium">
              City <span className="text-muted-foreground">(optional)</span>
            </label>
            <input
              id="su_city"
              type="text"
              autoComplete="address-level2"
              className={input}
              value={city}
              onChange={(e) => setCity(e.target.value)}
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <label htmlFor="su_pw" className="text-sm font-medium">
            Password (min {MIN_PASSWORD} characters)
          </label>
          <input
            id="su_pw"
            type="password"
            autoComplete="new-password"
            className={input}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          {tooShort && (
            <p className="text-xs text-destructive">At least {MIN_PASSWORD} characters.</p>
          )}
        </div>

        <div className="space-y-1.5">
          <label htmlFor="su_confirm" className="text-sm font-medium">
            Confirm password
          </label>
          <input
            id="su_confirm"
            type="password"
            autoComplete="new-password"
            className={input}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
          />
          {mismatch && <p className="text-xs text-destructive">Passwords don't match.</p>}
        </div>

        {error && (
          <div
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading || !canSubmit}
          className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-md bg-primary text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
        >
          {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          {loading ? "Creating account…" : "Create account"}
        </button>
      </form>

      <button
        type="button"
        onClick={onBack}
        className="inline-flex w-full items-center justify-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Back to sign in
      </button>
    </div>
  );
}
